<template>
    <div>
        <!-- MARKER ADDITIONAL INFO -->
        <p class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-2">
            {{ __('additional_info') }}
        </p>

        <v-select
            :value="entries"
            @input="onSelect"
            :options="options"
            multiple
            :filterable="false"
            :searchable="false"
            :getOptionKey="(option) => option.value"
            :placeholder="__('add_marker_data')"
            class="mb-3 additional-info-select"
        >
            <!-- Keep the placeholder visible at all times -->
            <template #search="{ attributes, events }">
                <input
                    class="vs__search"
                    v-bind="{
                        ...attributes,
                        placeholder: __('add_marker_data')
                    }"
                    v-on="events"
                />
            </template>

            <!-- Show the selected count next to the arrow -->
            <template #open-indicator="{ attributes }">
                <span v-if="entries.length" class="badge rounded-pill bg-secondary me-2">
                    {{ entries.length }}
                </span>
                <span v-bind="attributes">
                    <i class="fa-solid fa-chevron-down"></i>
                </span>
            </template>

            <!-- The checkmark/styling of selected options is driven by
                 vue-select's own `vs__dropdown-option--selected` class (see CSS). -->
            <template #option="option">
                <div
                    class="d-flex align-items-center justify-content-between additional-info-option"
                >
                    <span>{{ option.label }}</span>
                    <i class="fa-solid fa-check"></i>
                </div>
            </template>
        </v-select>

        <div class="table-responsive" v-if="entries.length">
            <table class="table table-hover table-borderless align-middle additional-info-table">
                <thead>
                    <tr class="text-smaller text-muted text-uppercase">
                        <th scope="col" class="border-bottom"></th>
                        <th scope="col" class="border-bottom text-center">
                            {{ __('background_color') }}
                        </th>
                        <th scope="col" class="border-bottom text-center">
                            {{ __('font_color') }}
                        </th>
                        <th scope="col" class="border-bottom text-center">{{ __('opacity') }} %</th>
                        <th scope="col" class="border-bottom"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in entries" :key="item.value">
                        <td class="fw-semibold">{{ item.label }}</td>
                        <td class="text-center">
                            <input
                                :value="item.backgroundColor"
                                @change="setField(index, 'backgroundColor', $event.target.value)"
                                type="color"
                                class="form-control form-control-color mx-auto"
                            />
                        </td>

                        <td class="text-center">
                            <input
                                :value="item.fontColor"
                                @change="setField(index, 'fontColor', $event.target.value)"
                                type="color"
                                class="form-control form-control-color mx-auto"
                            />
                        </td>

                        <td class="text-center">
                            <input
                                :value="item.fontOpacity"
                                @change="setField(index, 'fontOpacity', Number($event.target.value))"
                                type="number"
                                min="0"
                                max="100"
                                step="10"
                                class="form-control mx-auto additional-info-opacity"
                            />
                        </td>

                        <td class="text-end">
                            <span class="d-inline-flex align-items-center gap-3">
                                <i
                                    v-b-tooltip.hover
                                    :title="__('move_up')"
                                    class="fa-regular fa-arrow-up additional-info-action"
                                    :class="{ 'additional-info-action--disabled': index === 0 }"
                                    role="button"
                                    @click="moveEntry(index, -1)"
                                ></i>
                                <i
                                    v-b-tooltip.hover
                                    :title="__('move_down')"
                                    class="fa-regular fa-arrow-down additional-info-action"
                                    :class="{
                                        'additional-info-action--disabled':
                                            index === entries.length - 1
                                    }"
                                    role="button"
                                    @click="moveEntry(index, 1)"
                                ></i>
                                <i
                                    v-b-tooltip.hover
                                    :title="__('remove')"
                                    class="fa-regular fa-xmark additional-info-action additional-info-action--remove"
                                    role="button"
                                    @click="removeEntry(index)"
                                ></i>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
// Self-contained editor for the marker "additional data" list. It owns the option
// catalog, reads/writes the config store directly, and is fully immutable: every
// edit builds a brand-new array of brand-new entries and commits it. There is no
// local mirror, no deep watcher and no ready-flag — the store getter is reactive
// per-key (auto re-hydrates on config reset) and `commitChange` coalesces its own
// backend flush, so committing on each discrete edit is cheap.
const CONFIG_KEY = 'vehicleMarkerAdditionalData'

// Default style applied to a freshly added entry.
const DEFAULTS = { backgroundColor: '#ffffff', fontColor: '#000000', fontOpacity: 50 }

export default {
    name: 'MarkerAdditionalDataEditor',
    computed: {
        // Option catalog. Computed (not data) so the labels re-evaluate with the locale.
        options() {
            return [
                { value: 'speed', label: this.__('speed') },
                { value: 'altitude', label: this.__('altitude') },
                { value: 'rpm_engine', label: this.__('rpm_engine') },
                {
                    value: 'driver_full_name',
                    label: this.__('settings_section.map.additional_data.driver_full_name')
                },
                {
                    value: 'custom_code',
                    label: this.__('settings_section.map.additional_data.custom_code')
                }
            ]
        },
        // The list as rendered: read straight from the store, de-duplicated and
        // normalized (legacy shapes healed, label re-derived so it stays translated).
        // Reactive on the config key, so a config reset re-hydrates the table for free.
        entries() {
            const raw = this.$store.getters['config/value'](CONFIG_KEY)
            const list = Array.isArray(raw) ? raw : raw ? [raw] : []
            const seen = new Set()
            return list.map(this.normalize).filter((entry) => {
                if (seen.has(entry.value)) return false
                seen.add(entry.value)
                return true
            })
        }
    },
    methods: {
        // Gives an entry a consistent, reactive shape:
        // { value, label, backgroundColor, fontColor, fontOpacity }.
        // Handles the current object format, a freshly picked option ({ value, label }),
        // and the legacy bare-string format.
        normalize(entry) {
            const isLegacy = typeof entry === 'string'
            const value = isLegacy ? entry : entry.value
            const option = this.options.find((o) => o.value === value)

            // Legacy entries carry no style, so fall back to the old per-config color keys
            // (same keys LTruckMarker reads) so the marker looks identical after migration.
            const style = isLegacy
                ? {
                      backgroundColor: this.configValue('vehicleAdditionalInfoBackgroundColor'),
                      fontColor: this.configValue('vehicleAdditionalInfoFontColor'),
                      fontOpacity: Number(this.configValue('vehicleAdditionalInfoOpacity'))
                  }
                : entry

            return {
                ...DEFAULTS,
                ...style,
                value,
                label: option ? option.label : this.__(value)
            }
        },
        configValue(key) {
            return this.$store.getters['config/value'](key)
        },
        // Persist a new list. Stores only value + style (no label — it is presentation,
        // re-derived on read and kept translated).
        persist(list) {
            const value = list.map(({ value, backgroundColor, fontColor, fontOpacity }) => ({
                value,
                backgroundColor,
                fontColor,
                fontOpacity
            }))
            this.$store.dispatch('config/commitChange', { key: CONFIG_KEY, value })
        },
        // v-select emits the full new selection on add/remove. Kept entries arrive as our
        // own objects (style preserved via normalize's spread); a newly picked option
        // arrives as { value, label } and normalize applies the defaults.
        onSelect(selection) {
            this.persist(selection.map(this.normalize))
        },
        setField(index, field, value) {
            this.persist(this.entries.map((e, i) => (i === index ? { ...e, [field]: value } : e)))
        },
        removeEntry(index) {
            this.persist(this.entries.filter((_, i) => i !== index))
        },
        // Moves an entry one slot up (direction -1) or down (+1) by swapping it with
        // its neighbour, then persists the new order.
        moveEntry(index, direction) {
            const target = index + direction
            if (target < 0 || target >= this.entries.length) return

            const reordered = [...this.entries]
            const moved = reordered[index]
            reordered[index] = reordered[target]
            reordered[target] = moved
            this.persist(reordered)
        }
    }
}
</script>

<style lang="scss">
.additional-info-select {
    // Give the control a consistent height and vertically center the placeholder /
    // search input inside it (the input carries the always-visible placeholder).
    .vs__dropdown-toggle {
        min-height: 38px;
    }

    .vs__selected-options {
        align-items: center;
    }

    .vs__search,
    .vs__search:focus {
        margin-top: 0;
    }

    .vs__selected {
        display: none;
    }

    .additional-info-option {
        width: 100%;

        // Checkmark only shows on options vue-select marks as selected.
        i {
            display: none;
            color: var(--bs-success, #198754);
        }
    }

    .vs__dropdown-option--selected .additional-info-option {
        font-weight: 600;
        opacity: 0.85;

        i {
            display: inline;
        }
    }
}

.additional-info-table {
    --bs-table-bg: transparent;
    --bs-table-hover-bg: transparent;
    --bs-table-accent-bg: transparent;
    background-color: transparent !important;

    th,
    td,
    tr {
        background-color: transparent !important;
    }

    th,
    td {
        vertical-align: middle;
        padding: 0.5rem 0.75rem;
    }

    .additional-info-opacity {
        width: 90px;
    }

    .additional-info-action {
        color: #adb5bd;
        cursor: pointer;
        transition: color 0.15s ease-in-out;

        &:hover {
            color: var(--bs-secondary, #6c757d);
        }

        &.additional-info-action--remove:hover {
            color: var(--bs-danger, #dc3545);
        }

        &.additional-info-action--disabled {
            opacity: 0.35;
            pointer-events: none;
            cursor: default;
        }
    }
}
</style>
